/**
 * @fileoverview Character Picker Component
 * @description Allows user to select Opa or Oma character
 */

import React, { memo } from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  CharacterId,
  CharacterConfig,
  CHARACTER_CONFIGS,
  CharacterAbilitySettings,
  DEFAULT_CHARACTER_ABILITY_SETTINGS,
} from "../types";

// Import character images
const OPA_IMAGE = require("@/assets/games/opa_endless_runner.svg");
const OMA_IMAGE = require("@/assets/games/omam_endless_runner.svg");

interface CharacterPickerProps {
  /** Currently selected character */
  selectedCharacter: CharacterId;
  /** Callback when character is selected */
  onSelect: (characterId: CharacterId) => void;
  /** Custom ability settings from admin */
  abilitySettings?: CharacterAbilitySettings;
  /** Whether picker is disabled */
  disabled?: boolean;
}

/**
 * Character Card Component
 */
const CharacterCard = memo<{
  config: CharacterConfig;
  isSelected: boolean;
  onPress: () => void;
  abilitySettings?: CharacterAbilitySettings;
  disabled?: boolean;
}>(({ config, isSelected, onPress, abilitySettings, disabled }) => {
  const colors = useThemeColors();

  // Get customized ability values
  const getAbilityDescription = (): string => {
    if (config.id === "opa") {
      const multiplier =
        abilitySettings?.opa.jumpForceMultiplier ??
        DEFAULT_CHARACTER_ABILITY_SETTINGS.opa.jumpForceMultiplier;
      const maxJumps =
        abilitySettings?.opa.maxSpecialJumps ??
        DEFAULT_CHARACTER_ABILITY_SETTINGS.opa.maxSpecialJumps;
      const percentage = Math.round((multiplier - 1) * 100);
      return `Springt ${percentage}% hoger (max ${maxJumps}x)`;
    } else {
      const lives =
        abilitySettings?.oma.lives ?? DEFAULT_CHARACTER_ABILITY_SETTINGS.oma.lives;
      return `Kan ${lives} botsingen overleven`;
    }
  };

  const characterImage = config.id === "opa" ? OPA_IMAGE : OMA_IMAGE;

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={[
        styles.card,
        {
          backgroundColor: colors.surface,
          borderColor: isSelected ? colors.primary : colors.border,
          borderWidth: isSelected ? 2 : 1,
        },
        disabled && styles.cardDisabled,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`Selecteer ${config.name}`}
      accessibilityState={{ selected: isSelected }}
    >
      {/* Selection indicator */}
      {isSelected && (
        <View style={[styles.selectedBadge, { backgroundColor: colors.primary }]}>
          <Ionicons name="checkmark" size={14} color="#FFFFFF" />
        </View>
      )}

      {/* Character Image */}
      <View style={[styles.imageContainer, { backgroundColor: colors.surfaceSecondary }]}>
        <Image
          source={characterImage}
          style={styles.characterImage}
          resizeMode="contain"
        />
      </View>

      {/* Character Info */}
      <View style={styles.infoContainer}>
        <Text style={[styles.characterName, { color: colors.text }]}>
          {config.emoji} {config.name}
        </Text>

        <View style={styles.abilityContainer}>
          <View style={[styles.abilityBadge, { backgroundColor: `${colors.primary}20` }]}>
            <Ionicons
              name={config.id === "opa" ? "arrow-up-circle" : "heart"}
              size={14}
              color={colors.primary}
            />
            <Text style={[styles.abilityName, { color: colors.primary }]}>
              {config.abilityName}
            </Text>
          </View>
        </View>

        <Text style={[styles.abilityDescription, { color: colors.textSecondary }]}>
          {getAbilityDescription()}
        </Text>
      </View>
    </TouchableOpacity>
  );
});

CharacterCard.displayName = "CharacterCard";

/**
 * CharacterPicker Component
 */
export const CharacterPicker = memo<CharacterPickerProps>(
  ({
    selectedCharacter,
    onSelect,
    abilitySettings = DEFAULT_CHARACTER_ABILITY_SETTINGS,
    disabled = false,
  }) => {
    const colors = useThemeColors();

    return (
      <View style={styles.container}>
        <Text style={[styles.title, { color: colors.text }]}>
          Kies je karakter
        </Text>

        <View style={styles.cardsContainer}>
          {(["opa", "oma"] as CharacterId[]).map((characterId) => (
            <CharacterCard
              key={characterId}
              config={CHARACTER_CONFIGS[characterId]}
              isSelected={selectedCharacter === characterId}
              onPress={() => onSelect(characterId)}
              abilitySettings={abilitySettings}
              disabled={disabled}
            />
          ))}
        </View>
      </View>
    );
  }
);

CharacterPicker.displayName = "CharacterPicker";

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
  },
  cardsContainer: {
    flexDirection: "row",
    gap: 12,
  },
  card: {
    flex: 1,
    borderRadius: 16,
    padding: 12,
    alignItems: "center",
    position: "relative",
  },
  cardDisabled: {
    opacity: 0.5,
  },
  selectedBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  imageContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    overflow: "hidden",
  },
  characterImage: {
    width: 60,
    height: 60,
  },
  infoContainer: {
    alignItems: "center",
    gap: 6,
  },
  characterName: {
    fontSize: 18,
    fontWeight: "700",
  },
  abilityContainer: {
    marginTop: 2,
  },
  abilityBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  abilityName: {
    fontSize: 12,
    fontWeight: "600",
  },
  abilityDescription: {
    fontSize: 11,
    textAlign: "center",
    lineHeight: 14,
  },
});

